import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { useGetCurrentUser, useGetUserSelectedCompanyId } from '@queries/users.query';
import { type QueryKey, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AssessmentHistorySelectDto,
  AssessmentSelectDto,
  AssessmentType,
  AssessmentsSelectDto,
  DomainSelectDto,
  DomainsSelectDto,
  TechnologiesListSelectDto,
  TechnologiesSelectDto,
  TechnologyCreateDto,
  TechnologyCreateResponseDto,
  TechnologyListSelectDto,
  TechnologySelectDto,
  TechnologySimilarTechnologiesUpsertDto,
  TechnologyTrlAnalysisSelectDto,
  TechnologyTrlAnalysisSummaryUpsertDto,
  TechnologyTrlProjectionLlmQueryDto,
  TechnologyTrlProjectionSelectDto,
  TechnologyUpdateDto,
  UseSelectDto,
  UsesSelectDto,
} from '../types/api.types.ts';

export const useTechnologiesQueryOptions = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isInnovator } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = Boolean(isAuthenticated && isInnovator && selectedCompanyId);

  return queryOptions<TechnologySelectDto[], ApiErrorWrapper>({
    enabled,
    queryKey: enabled ? queryKeys.technology.getTechnologies : queryKeys.placeholders.default,
    queryFn: async () => {
      const result = await apiGet<TechnologiesSelectDto>({
        path: '/v1/innovator/technologies',
      });
      return result.technologies;
    },
  });
};

export const useTechnologiesListQueryOptions = () => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isInnovator } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = Boolean(isAuthenticated && isInnovator && selectedCompanyId);

  return queryOptions<TechnologyListSelectDto[], ApiErrorWrapper>({
    enabled,
    queryKey: enabled ? queryKeys.technology.getTechnologiesList : queryKeys.placeholders.default,
    queryFn: async () => {
      const result = await apiGet<TechnologiesListSelectDto>({
        path: '/v1/innovator/technologies/list',
      });
      return result.technologies;
    },
  });
};

export const useTechnologyQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { data: user, isExpert, isIntel, isInnovator, isSelectedCompanyParentOrganization } = useGetCurrentUser();
  const { isAuthenticated } = useAuth0();
  const selectedCompanyId = useGetUserSelectedCompanyId();

  const enabled =
    Boolean(user) &&
    typeof technologyId === 'string' &&
    technologyId.length > 0 &&
    Boolean(isAuthenticated) &&
    (isExpert || isIntel || (isInnovator && Boolean(selectedCompanyId)));

  return queryOptions<TechnologySelectDto, ApiErrorWrapper>({
    enabled,
    queryKey: enabled ? queryKeys.technology.getTechnology(technologyId) : queryKeys.placeholders.default,
    queryFn: async () => {
      if (isInnovator) {
        if (isSelectedCompanyParentOrganization) {
          return await apiGet<TechnologySelectDto>({
            path: `/v1/innovator/companies/technologies/${technologyId}`,
          });
        }
        return await apiGet<TechnologySelectDto>({
          path: `/v1/innovator/technologies/${technologyId}`,
        });
      } else if (isExpert) {
        return await apiGet<TechnologySelectDto>({
          path: `/v1/expert/technologies/${technologyId}`,
        });
      } else if (isIntel) {
        return await apiGet<TechnologySelectDto>({
          path: `/v1/intel/technologies/${technologyId}`,
        });
      }
      throw new Error('User role not supported for technology access');
    },
  });
};

export const useGetTechnology = (technologyId?: string) => {
  const technologyQueryOptions = useTechnologyQueryOptions(technologyId);
  return useQuery(technologyQueryOptions);
};

export const useCreateTechnology = () => {
  const { apiPost } = useApi();

  return useMutation<TechnologyCreateResponseDto, ApiErrorWrapper, TechnologyCreateDto>({
    mutationFn: async (formData) => {
      return await apiPost<TechnologyCreateResponseDto>({
        path: '/v1/innovator/technologies',
        body: formData,
      });
    },
  });
};

export const useGetTechnologyDomains = () => {
  const { apiGet } = useApi();

  return useQuery<DomainSelectDto[], ApiErrorWrapper>({
    queryFn: async () => {
      const result = await apiGet<DomainsSelectDto>({
        path: '/v1/data/domains',
      });
      return result.domains;
    },
    queryKey: queryKeys.technology.getTechnologyDomains,
  });
};

export const useGetTechnologyUses = () => {
  const { apiGet } = useApi();

  return useQuery<UseSelectDto[], ApiErrorWrapper>({
    queryFn: async () => {
      const result = await apiGet<UsesSelectDto>({
        path: '/v1/data/uses',
      });
      return result.uses;
    },
    queryKey: queryKeys.technology.getTechnologyUses,
  });
};

export const useUpdateTechnology = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: TechnologySelectDto, variables: TechnologyUpdateDto & { id: string }) => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologies,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologiesList,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnology(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getAssessmentHistory(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologyTrlAssessment(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologyMrlAssessment(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologyUsesWithAssessments(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologyTrlProjection(data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.assessments.getAssessments([AssessmentType.MRL], data.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.assessments.getAssessments([AssessmentType.TRL], data.id),
    });

    if (variables.productIds !== undefined) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.product.getProducts(),
      });
    }
  };

  return useMutation({
    mutationFn: async (data: TechnologyUpdateDto & { id: string }) => {
      return await apiPatch<TechnologySelectDto>({
        path: `/v1/innovator/technologies/${data.id}`,
        body: data,
      });
    },
    onSuccess,
  });
};

export const useDeleteTechnology = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  const removeTechnologyFromCachedList = <T extends { id: string }>(params: {
    queryKey: QueryKey;
    technologyId: string;
  }): void => {
    const { queryKey, technologyId } = params;

    queryClient.setQueryData<T[] | undefined>(queryKey, (cached) => {
      if (!cached) return cached;
      return cached.filter((item) => item.id !== technologyId);
    });
  };

  const silenceTechnologyScopedQueries = async (technologyId: string): Promise<void> => {
    const rootQueryKeys: QueryKey[] = [
      ['technology'],
      ['assessments'],
      ['missionAssessments'],
      ['missionAssessmentDrafts'],
      ['missionAssessmentRequests'],
      ['product'],
      ['survey'],
    ];

    await Promise.all(
      rootQueryKeys.map(async (rootKey) => {
        const filters = {
          predicate: (query: { queryHash: string }) => query.queryHash.includes(technologyId),
          queryKey: rootKey,
        };

        await queryClient.cancelQueries(filters);
        await queryClient.invalidateQueries({ ...filters, refetchType: 'none' });
      }),
    );
  };

  const onSuccess = async (data: { id: string }) => {
    const { id } = data;

    await silenceTechnologyScopedQueries(id);

    removeTechnologyFromCachedList<TechnologySelectDto>({
      queryKey: queryKeys.technology.getTechnologies,
      technologyId: id,
    });
    removeTechnologyFromCachedList<TechnologyListSelectDto>({
      queryKey: queryKeys.technology.getTechnologiesList,
      technologyId: id,
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologies,
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.technology.getTechnologiesList,
    });
  };

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await apiDelete({
        path: `/v1/innovator/technologies/${id}`,
      });
      return { id };
    },
    onSuccess,
  });
};

export const useGetAssessmentHistoryQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const { isAuthenticated } = useAuth0();
  const enabled = technologyId !== undefined && Boolean(isAuthenticated) && Boolean(isExpert || isInnovator || isIntel);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return queryOptions<AssessmentHistorySelectDto, ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      if (!technologyId) throw new Error('Technology ID is required');
      const path = isInnovator
        ? `/v1/innovator/technologies/${technologyId}/assessment-history`
        : isExpert
          ? `/v1/expert/technologies/${technologyId}/assessment-history`
          : isIntel
            ? `/v1/intel/technologies/${technologyId}/assessment-history`
            : undefined;

      if (!path) {
        throw new Error('User role not supported for assessment history access');
      }
      return await apiGet<AssessmentHistorySelectDto>({
        path,
        query: { tz: userTimeZone },
      });
    },
    queryKey: enabled ? queryKeys.technology.getAssessmentHistory(technologyId) : queryKeys.placeholders.default,
  });
};

export const useGetAssessmentHistory = (technologyId?: string) => {
  const assessmentHistoryQueryOptions = useGetAssessmentHistoryQueryOptions(technologyId);
  return useQuery(assessmentHistoryQueryOptions);
};

type UseGetTechnologyTrlAssessmentOptions = {
  enabled?: boolean;
};

export const useGetTechnologyTrlAssessmentQueryOptions = (
  technologyId?: string,
  options: UseGetTechnologyTrlAssessmentOptions = {},
) => {
  const { enabled: isQueryEnabled = true } = options;
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const enabled = Boolean(isQueryEnabled && isAuthenticated && technologyId);

  return queryOptions<AssessmentSelectDto | null, ApiErrorWrapper>({
    enabled,
    queryKey:
      enabled && technologyId
        ? queryKeys.technology.getTechnologyTrlAssessment(technologyId)
        : queryKeys.placeholders.default,
    queryFn: async () => {
      if (!technologyId) return null;
      const path = isInnovator
        ? '/v1/innovator/assessments'
        : isExpert
          ? '/v1/expert/assessments'
          : isIntel
            ? '/v1/intel/assessments'
            : undefined;

      if (!path) {
        throw new Error('User role not supported for assessment access');
      }
      const result = await apiGet<AssessmentsSelectDto>({
        path,
        query: { technologyId, type: 'TRL', featured: true },
      });
      return result.assessments[0] || null;
    },
  });
};

export const useGetTechnologyTrlAssessment = (
  technologyId?: string,
  options: UseGetTechnologyTrlAssessmentOptions = {},
) => {
  const trlAssessmentQueryOptions = useGetTechnologyTrlAssessmentQueryOptions(technologyId, options);
  return useQuery(trlAssessmentQueryOptions);
};

export const useGetTechnologyMrlAssessmentQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const enabled = Boolean(isAuthenticated && technologyId);

  return queryOptions<AssessmentSelectDto | null, ApiErrorWrapper>({
    enabled,
    queryKey:
      enabled && technologyId
        ? queryKeys.technology.getTechnologyMrlAssessment(technologyId)
        : queryKeys.placeholders.default,
    queryFn: async () => {
      if (!technologyId) return null;
      const path = isInnovator
        ? '/v1/innovator/assessments'
        : isExpert
          ? '/v1/expert/assessments'
          : isIntel
            ? '/v1/intel/assessments'
            : undefined;

      if (!path) {
        throw new Error('User role not supported for assessment access');
      }
      const result = await apiGet<AssessmentsSelectDto>({
        path,
        query: { technologyId, type: 'MRL', featured: true },
      });
      return result.assessments[0] || null;
    },
  });
};

export const useGetTechnologyMrlAssessment = (technologyId?: string) => {
  const mrlAssessmentQueryOptions = useGetTechnologyMrlAssessmentQueryOptions(technologyId);
  return useQuery(mrlAssessmentQueryOptions);
};

export const useGetTechnologyTrlProjectionQueryOptions = (technologyId?: string) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const enabled = Boolean(isAuthenticated && technologyId);

  return queryOptions<TechnologyTrlProjectionSelectDto, ApiErrorWrapper>({
    enabled,
    queryKey:
      enabled && technologyId
        ? queryKeys.technology.getTechnologyTrlProjection(technologyId)
        : queryKeys.placeholders.default,
    queryFn: async () => {
      if (!technologyId) {
        throw new Error('Technology id is required for TRL projection');
      }

      const path = isInnovator
        ? `/v1/innovator/technologies/${technologyId}/trl-projection`
        : isExpert
          ? `/v1/expert/technologies/${technologyId}/trl-projection`
          : isIntel
            ? `/v1/intel/technologies/${technologyId}/trl-projection`
            : undefined;

      if (!path) {
        throw new Error('User role not supported for TRL projection access');
      }

      return await apiGet<TechnologyTrlProjectionSelectDto>({
        path,
      });
    },
  });
};

export const useGetTechnologyTrlProjection = (technologyId?: string) => {
  const technologyTrlProjectionQueryOptions = useGetTechnologyTrlProjectionQueryOptions(technologyId);
  return useQuery(technologyTrlProjectionQueryOptions);
};

export const useSaveTechnologyTrlAnalysisSummary = () => {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyTrlAnalysisSelectDto,
    ApiErrorWrapper,
    TechnologyTrlAnalysisSummaryUpsertDto & { technologyId: string }
  >({
    mutationFn: async (data) => {
      const { technologyId, ...requestBody } = data;
      return await apiPut<TechnologyTrlAnalysisSelectDto>({
        path: `/v1/innovator/technologies/${technologyId}/trl-analysis/summary`,
        body: requestBody,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlAnalysis(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlAssessment(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnology(variables.technologyId),
      });
      // Invalidate assessments list to refresh old assessment data
      queryClient.invalidateQueries({
        queryKey: queryKeys.assessments.getAssessments([AssessmentType.TRL], variables.technologyId),
      });
    },
  });
};

export const useSaveSimilarTechnologies = () => {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyTrlAnalysisSelectDto,
    ApiErrorWrapper,
    TechnologySimilarTechnologiesUpsertDto & { technologyId: string }
  >({
    mutationFn: async (data) => {
      const { technologyId, ...requestBody } = data;
      return await apiPut<TechnologyTrlAnalysisSelectDto>({
        path: `/v1/innovator/technologies/${technologyId}/trl-analysis/similar-technologies`,
        body: requestBody,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlAnalysis(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlAssessment(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnology(variables.technologyId),
      });
      // Invalidate assessments list to refresh old assessment data
      queryClient.invalidateQueries({
        queryKey: queryKeys.assessments.getAssessments([AssessmentType.TRL], variables.technologyId),
      });
    },
  });
};

export const useGenerateTechnologyTrlProjectionAnalysis = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    TechnologyTrlProjectionSelectDto,
    ApiErrorWrapper,
    TechnologyTrlProjectionLlmQueryDto & { technologyId: string }
  >({
    mutationFn: async (data) => {
      const { technologyId, ...requestBody } = data;
      return await apiPost<TechnologyTrlProjectionSelectDto>({
        body: requestBody,
        path: `/v1/innovator/technologies/${technologyId}/trl-projection/analysis`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlProjection(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnology(variables.technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.technology.getTechnologyTrlAssessment(variables.technologyId),
      });
    },
  });
};
